import { useQuery } from "@apollo/client";
import { useCallback, useEffect, useMemo, useState } from "react";

import OutlineButton from "@/components/buttons/outline-button";
import DownloadApp from "@/components/download-app";
import GalleryThumbnailsModal from "@/components/gallery-thumbnails-modal";
import GameCard from "@/components/game-card";
import type { PhotoType } from "@/components/photos-list";
import useAuth from "@/hooks/use-auth";
import useShare from "@/hooks/use-share";
import { GameStatusEnum } from "@/pages/game/types";
import GameTile from "@/pages/search/list/games/game-tile";

import type { LocationSetType } from "../company/types";
import Description from "./description";
import { QUERY } from "./graphql";
import Header from "./header";
import Hero from "./hero";
import useMetaProps from "./hooks/use-meta-props";
import RightSidebar from "./right-sidebar";
import type { DataType, VariablesType } from "./types";

export interface LocationProps {
  data: DataType;
}

export default function Location({ data: serverData }: LocationProps) {
  const [showClosedRooms, setShowClosedRooms] = useState(false);

  const { isUser, queryContext } = useAuth();
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const { data } = useQuery<DataType, VariablesType>(QUERY, {
    skip: !isUser,
    ...queryContext({
      companySlug: serverData.company.slug,
      isUser,
      locationSlug: serverData.company.locations[0].slug,
    }),
  });

  const isClosedGame = useCallback(
    (game: { status: keyof typeof GameStatusEnum }) =>
      [GameStatusEnum.PC, GameStatusEnum.TC].includes(
        game.status as typeof GameStatusEnum.PC | typeof GameStatusEnum.TC
      ),
    []
  );

  const { company, location, otherLocations, url } = useMemo(() => {
    const company = (data || serverData).company;
    const [location] = company.locations;

    const isClosedLocation = (l: LocationSetType) =>
      l.games.every((g) => isClosedGame(g));

    return {
      company,
      location,
      otherLocations: company.locationSet
        .filter((l) => l.id !== location.id)
        .sort((a, b) =>
          (a.displayName || a.company.name)
            .toLowerCase()
            .localeCompare((b.displayName || b.company.name).toLowerCase())
        )
        .sort(
          (a, b) => Number(isClosedLocation(a)) - Number(isClosedLocation(b))
        ),
      url: `${process.env.APP_URL}/location/${company.slug}/${location.slug}`,
    };
  }, [serverData, data, isClosedGame]);

  const { description, title } = useMetaProps(location);
  const { onShare } = useShare({
    text: description,
    title,
    url,
  });

  const { games, hasClosedRooms, hasOpenRooms } = useMemo(() => {
    const { games } = location;

    return {
      games,
      hasClosedRooms: games.some(isClosedGame),
      hasOpenRooms: games.some((game) => !isClosedGame(game)),
    };
  }, [location, isClosedGame]);

  const openGallery = () => {
    setIsGalleryOpen(true);
  };

  const handleClosedRoomToggle = () => {
    setShowClosedRooms((prev) => !prev);
  };

  useEffect(() => {
    setShowClosedRooms(!hasOpenRooms);
  }, [hasOpenRooms]);

  const photos = useMemo(
    () =>
      Object.values(
        games.reduce(
          (accum, game) =>
            !hasOpenRooms ||
            game.status === GameStatusEnum.OP ||
            game.status === GameStatusEnum.CS
              ? (game.gameimageSet || []).reduce((a, photo) => {
                  if (photo.imageId) {
                    a[photo.imageId] = photo;
                  }
                  return a;
                }, accum)
              : accum,
          {} as Record<string, PhotoType>
        )
      ),
    [games, hasOpenRooms]
  );

  const filteredGames = useMemo(
    () =>
      games.filter(
        (game) =>
          showClosedRooms ||
          game.status === GameStatusEnum.OP ||
          game.status === GameStatusEnum.CS
      ),
    [games, showClosedRooms]
  );

  return (
    <>
      <Header onShare={onShare} />
      <div itemScope itemType="http://schema.org/LocalBusiness">
        <Hero
          company={company}
          location={location}
          openGallery={openGallery}
          photos={photos}
        />
        <section className="container md:mt-15">
          <div className="mt-6 grid grid-cols-1 md:grid-cols-12">
            <div className="md:col-span-6">
              <div className="mb-5 flex justify-between">
                <h2 className="text-16 md:text-24">Experiences</h2>
                {hasOpenRooms && hasClosedRooms && (
                  <OutlineButton
                    label={showClosedRooms ? "Hide closed" : "Show closed"}
                    onClick={handleClosedRoomToggle}
                  />
                )}
              </div>
              <div className="flex flex-col items-stretch overflow-hidden *:not-first:mt-2.5 *:not-first:border-white/8 *:not-first:border-t *:not-first:pt-2.5 md:hidden md:gap-0">
                {filteredGames.map((game) => (
                  <GameTile
                    game={{
                      ...game,
                      hasBookings: false,
                      isPromoted: false,
                      promotedBidId: "",
                    }}
                    key={game.id}
                    withoutCompany={true}
                  />
                ))}
              </div>
              <div className="hidden grid-cols-2 gap-5 md:grid lg:gap-10">
                {filteredGames.map((game) => (
                  <GameCard game={game} key={game.id} />
                ))}
              </div>
              {location.description && (
                <Description description={location.description} />
              )}
            </div>
            <RightSidebar
              coupons={location.coupons}
              location={location}
              otherLocations={otherLocations}
              photos={photos}
            />
          </div>
          <DownloadApp className="my-20 standalone:hidden pt-[167px] md:my-35 md:pt-[183px] md:pb-[183px]" />
        </section>
        <GalleryThumbnailsModal
          onOpenChange={setIsGalleryOpen}
          open={isGalleryOpen}
          photos={photos}
        />
      </div>
    </>
  );
}
